import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { BatchedRecording, saveRecordings } from "lib/build/recordings";
import { LogItem, Result, validate, Value } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter,
	Legend,
	ValidationMessage
} from "src/common/components/Form";
import {
	GridColumn,
	GridRow,
	SplitColumn,
	SplitRow
} from "src/common/components/Grid";
import { FullCardPreloader } from "src/common/components/Preloader";
import { Switch } from "src/common/components/Switch";
import { getApiErrorData, isApiErrorWithCode } from "src/common/utils/api";
import { isTodayOrBefore } from "src/common/utils/dates";
import { saveFiles } from "src/common/utils/files";
import { useConfirm } from "src/features/confirm";
import { useRefetchEvents as useRefetchEventsDefault } from "src/features/events";
import { Attachments } from "src/features/forms/components/Attachments";
import { FieldsetRenderer } from "src/features/forms/components/FieldsetRenderer";
import { FieldsetSkeleton } from "src/features/forms/components/FieldsetSkeleton";
import { Timestamp } from "src/features/forms/components/Timestamp";
import { FormContextProvider } from "src/features/forms/context/FormContext";
import { useGetDefaultTimestamp } from "src/features/forms/hooks/useGetDefaultTimestamp";
import { useValidatorOptions } from "src/features/forms/hooks/useValidatorOptions";
import { MiniFormData } from "src/features/forms/types";
import { mapLogItemsToEventQueries } from "src/features/forms/utils/mapLogItemsToEventQueries";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { genericMessages } from "src/messages/generic";

import "./MiniFormList.css";

const messages = defineMessages({
	attachments: {
		id: "miniform_list.attachments",
		defaultMessage: "Leletek, mellékletek"
	},
	success: {
		id: "miniform_list.save_list_success",
		defaultMessage: "Az adatok rögzítése sikeresen megtörtént"
	},
	versionMismatchError: {
		id: "miniform_list.version_mismatch_error",
		defaultMessage:
			'Ezzel az időponttal a(z) "{form}" űrlap már kitöltésre került. Szeretné felülírni az előző mentést?'
	}
});

const FormListContext = React.createContext<FormListState>({
	items: [],
	updateItem() {}
});

interface FormListState {
	items: FormListItemState[];

	updateItem(index: number, item: Partial<FormListItemState>): void;
}

interface FormListItemState {
	isSkipped: boolean;
	form: MiniFormData;
	version?: string;
	result: Result;
}

interface MiniFormListProps {
	patientId: string;
	forms: MiniFormData[];
	columns?: number;
}

interface MiniFormListState {
	isSubmitted: boolean;
	isSaving: boolean;
	timestamp: string | undefined;
	files: File[];
}

export const MiniFormList: React.FunctionComponent<MiniFormListProps> = ({
	patientId,
	forms,
	columns
}) => {
	const navigate = useNavigate();
	const confirm = useConfirm();

	const { org, mod } = useOrgModParams();

	const { formatMessage } = useIntl();
	const createMessage = useCreateMessage();
	const createGenericError = useCreateGenericError();
	const refetchEvents = useRefetchEvents(patientId);

	const showTimeInput = React.useMemo(
		() => forms.every(form => form.showTimeInput),
		[forms]
	);

	const [{ isSubmitted, isSaving, timestamp, files }, setState] =
		useFormListState(forms);

	const { items, updateItem } = useFormList(forms);
	const isValid = useIsValid(timestamp, items);

	const callbackRef = React.useRef({
		org,
		mod,
		patientId,
		isSubmitted,
		isSaving,
		timestamp,
		files,
		items,
		isValid
	});

	React.useEffect(() => {
		callbackRef.current.org = org;
	}, [org]);

	React.useEffect(() => {
		callbackRef.current.mod = mod;
	}, [mod]);

	React.useEffect(() => {
		callbackRef.current.patientId = patientId;
	}, [patientId]);

	React.useEffect(() => {
		callbackRef.current.isSubmitted = isSubmitted;
	}, [isSubmitted]);

	React.useEffect(() => {
		callbackRef.current.isSaving = isSaving;
	}, [isSaving]);

	React.useEffect(() => {
		callbackRef.current.timestamp = timestamp;
	}, [timestamp]);

	React.useEffect(() => {
		callbackRef.current.files = files;
	}, [files]);

	React.useEffect(() => {
		callbackRef.current.items = items;
	}, [items]);

	React.useEffect(() => {
		callbackRef.current.isValid = isValid;
	}, [isValid]);

	const onChangeTimestamp = React.useCallback(
		(timestamp: string | undefined): void => {
			setState(state => ({ ...state, timestamp }));
		},
		[setState]
	);

	const onChangeFiles = React.useCallback(
		(attachments: File[]): void => {
			setState(state => ({ ...state, files: attachments }));
		},
		[setState]
	);

	const saveRecordingsInner = React.useCallback(
		async (data: BatchedRecording[]): Promise<{ isSaved: boolean }> => {
			const {
				current: { org, mod, patientId, items }
			} = callbackRef;
			try {
				await saveRecordings({ org, mod, patientId, data });
				return { isSaved: true };
			} catch (error) {
				if (
					!isApiErrorWithCode<string>(
						error,
						"recordings.save.failed.concurrent"
					)
				) {
					throw error;
				}
				const { payload: version } = getApiErrorData(error);
				const form = version.split("|")[1];
				const item = items.find(item => item.form.id === form);
				if (item === undefined) {
					throw error;
				}
				const msg = formatMessage(messages.versionMismatchError, {
					form: formatMessage(item.form.title)
				});
				if ((await confirm({ content: msg })) === true) {
					return await saveRecordingsInner(
						updateVersion(data, form, version)
					);
				}
			}
			return { isSaved: false };
		},
		[confirm, formatMessage]
	);

	const onSubmit = React.useCallback(async (): Promise<void> => {
		const {
			current: { org, mod, patientId, timestamp, files, items, isValid }
		} = callbackRef;

		setState(state => ({ ...state, isSubmitted: true }));
		if (!isValid || timestamp === undefined) {
			return;
		}
		setState(state => ({ ...state, isSaving: true }));
		try {
			const data = await prepareDataToSave({
				org,
				mod,
				patientId,
				timestamp,
				files,
				items
			});
			const { isSaved } = await saveRecordingsInner(data);
			setState(state => ({ ...state, isSaving: false }));
			if (isSaved) {
				createMessage(messages.success);
				refetchEvents(items);
				navigate(`/${org}/${mod}/patients/${patientId}/dashboard`);
			}
		} catch {
			setState(state => ({ ...state, isSaving: false }));
			createGenericError();
		}
	}, [
		navigate,
		createMessage,
		createGenericError,
		setState,
		saveRecordingsInner,
		refetchEvents
	]);

	return (
		<FormListContext.Provider value={{ items, updateItem: updateItem }}>
			<Form
				className="mini-form-list"
				isSubmitted={isSubmitted}
				onSubmit={onSubmit}
			>
				<FormBody>
					<Fieldset>
						<GridRow>
							<GridColumn columnSize={6}>
								<Timestamp
									showTimeInput={showTimeInput}
									timestamp={timestamp}
									onChangeTimestamp={onChangeTimestamp}
								/>
							</GridColumn>
						</GridRow>
					</Fieldset>
					{isSaving ? (
						<Fieldset>
							<FullCardPreloader />
						</Fieldset>
					) : columns === 2 ? (
						<GridRow>
							<GridColumn columnSize={6}>
								{forms.map((form, index) =>
									index % 2 === 0 ? (
										<MiniFormListItem
											key={index}
											index={index}
											{...form}
										/>
									) : null
								)}
							</GridColumn>
							<GridColumn columnSize={6}>
								{forms.map((form, index) =>
									index % 2 === 0 ? null : (
										<MiniFormListItem
											key={index}
											index={index}
											{...form}
										/>
									)
								)}
							</GridColumn>
						</GridRow>
					) : (
						forms.map((form, index) => (
							<MiniFormListItem
								key={index}
								index={index}
								{...form}
							/>
						))
					)}
					{forms.some(
						({ showAttachments = true }) => showAttachments
					) && (
						<Fieldset>
							<Attachments
								unsavedFiles={files}
								onChangeUnsavedFiles={onChangeFiles}
							/>
						</Fieldset>
					)}
				</FormBody>
				<FormFooter>
					<SplitRow>
						<SplitColumn side="left">
							<PatientLink
								to="/dashboard"
								className="button button--secondary"
							>
								<FormattedMessage {...genericMessages.cancel} />
							</PatientLink>
						</SplitColumn>
						<SplitColumn side="right">
							{isSubmitted && !isValid && (
								<ValidationMessage>
									<FormattedMessage
										{...genericMessages.fixValidationErrors}
									/>
								</ValidationMessage>
							)}
							<Button type="submit" isLoading={isSaving}>
								<FormattedMessage {...genericMessages.save} />
							</Button>
						</SplitColumn>
					</SplitRow>
				</FormFooter>
			</Form>
		</FormListContext.Provider>
	);
};

const MiniFormListItem: React.FunctionComponent<
	{ index: number } & MiniFormData
> = ({ index, title, fieldset }) => {
	const { isSkipped, setIsSkipped } = useIsSkipped(index);
	const formState = useFormListItem(index);
	return (
		<FormContextProvider {...formState}>
			<Fieldset>
				<Legend>
					<Switch
						checked={!isSkipped}
						onChangeChecked={value => setIsSkipped(!value)}
					/>
					<FormattedMessage {...title} />
				</Legend>
				{!isSkipped &&
					(formState.isInitialized ? (
						<FieldsetRenderer {...fieldset} />
					) : (
						<FieldsetSkeleton />
					))}
			</Fieldset>
		</FormContextProvider>
	);
};

function useFormListState(
	forms: MiniFormData[]
): readonly [
	MiniFormListState,
	(
		valueOrFn:
			| MiniFormListState
			| ((state: MiniFormListState) => MiniFormListState)
	) => void
] {
	const getDefaultTimestamp = useGetDefaultTimestamp();
	return React.useState<MiniFormListState>({
		isSubmitted: false,
		isSaving: false,
		timestamp: getDefaultTimestamp({
			showTimeInput: forms.every(
				({ showTimeInput }) => showTimeInput === true
			),
			applyCurrentDate: forms.every(
				({ applyCurrentDate }) => applyCurrentDate === true
			),
			applyDateOfBirth: forms.every(
				({ applyDateOfBirth }) => applyDateOfBirth === true
			)
		}),
		files: []
	});
}

const emptyItem = { isSkipped: false, result: { value: {} } };

function useFormList(forms: MiniFormData[]): {
	items: FormListItemState[];
	updateItem(index: number, item: Partial<FormListItemState>): void;
} {
	const [items, dispatch] = React.useReducer(
		formListReducer,
		forms.map(form => ({ ...emptyItem, form }))
	);

	const updateItem = React.useCallback(
		(index: number, item: Partial<FormListItemState>) => {
			dispatch({ index, item });
		},
		[]
	);

	return { items, updateItem };
}

function formListReducer(
	state: FormListItemState[],
	{ index, item }: { index: number; item: Partial<FormListItemState> }
): FormListItemState[] {
	const out = state.slice();
	out[index] = { ...out[index], ...item };
	return out;
}

function useIsValid(
	timestamp: string | undefined,
	items: FormListItemState[]
): boolean {
	return React.useMemo(
		() =>
			timestamp !== undefined &&
			isTodayOrBefore(timestamp) &&
			items.every(
				({ isSkipped, result: { errors = [] } }) =>
					isSkipped || errors.length === 0
			),
		[timestamp, items]
	);
}

function useRefetchEvents(
	patientId: string
): (items: FormListItemState[]) => void {
	const refetchEvents = useRefetchEventsDefault(patientId);
	return React.useCallback(
		items => {
			for (const {
				isSkipped,
				result: { messages = [] }
			} of items) {
				if (!isSkipped) {
					for (const query of mapLogItemsToEventQueries(messages)) {
						refetchEvents(query);
					}
				}
			}
		},
		[refetchEvents]
	);
}

function useIsSkipped(index: number): {
	isSkipped: boolean;
	setIsSkipped(isSkipped: boolean): void;
} {
	const { items, updateItem } = React.useContext(FormListContext);

	const item = items[index];
	const { isSkipped } = item;

	const indexRef = React.useRef(index);
	React.useEffect(() => {
		indexRef.current = index;
	}, [index]);

	const setIsSkipped = React.useCallback(
		(isSkipped: boolean) => {
			updateItem(indexRef.current, { isSkipped });
		},
		[updateItem]
	);

	return { isSkipped, setIsSkipped };
}

const emptyValue: Value = {};

function useFormListItem(index: number): {
	isInitialized: boolean;
	value: Value;
	setValue(valueOrFn: Value | ((value: Value) => Value)): Promise<void>;
	messages: LogItem[];
	warnings: LogItem[];
	errors: LogItem[];
} {
	const [isInitialized, setInitialized] = React.useState(false);

	const { items, updateItem } = React.useContext(FormListContext);

	const item = items[index];
	const { form, result } = item;
	const { validator, initialValue = emptyValue } = form;

	const { value, messages = [], warnings = [], errors = [] } = result;

	const indexRef = React.useRef(index);
	React.useEffect(() => {
		indexRef.current = index;
	}, [index]);

	const validatorRef = React.useRef(validator);
	React.useEffect(() => {
		validatorRef.current = validator;
	}, [validator]);

	const valueRef = React.useRef(value);
	React.useEffect(() => {
		valueRef.current = value;
	}, [value]);

	const options = useValidatorOptions();

	const setValue = React.useCallback(
		async (
			valueOrFn: Value | ((value: Value) => Value),
			signal?: AbortSignal
		) => {
			const result = await validate({
				validator: validatorRef.current,
				value:
					typeof valueOrFn === "function"
						? valueOrFn(valueRef.current)
						: valueOrFn,
				options
			});
			if (signal === undefined || !signal.aborted) {
				setInitialized(true);
				updateItem(indexRef.current, { result });
			}
		},
		[updateItem, options]
	);

	React.useEffect(() => {
		const controller = new AbortController();
		setValue(initialValue, controller.signal);
		return () => {
			controller.abort();
		};
	}, [setValue, initialValue]);

	return { isInitialized, value, setValue, messages, warnings, errors };
}

async function prepareDataToSave({
	org,
	mod,
	patientId,
	timestamp,
	files,
	items
}: {
	org: string;
	mod: string;
	patientId: string;
	timestamp: string;
	files: File[];
	items: FormListItemState[];
}): Promise<BatchedRecording[]> {
	const data = [];
	const attachments = await saveFiles({
		org,
		mod,
		patientId,
		files
	});
	for (const {
		isSkipped,
		form: { id: form, schemaId: schema, showAttachments = true },
		result: { value }
	} of items) {
		if (isSkipped) {
			continue;
		}
		data.push({
			form,
			schema: schema ? schema : form,
			timestamp,
			value,
			attachments: showAttachments ? attachments : []
		});
	}
	return data;
}

function updateVersion(
	data: BatchedRecording[],
	form: string,
	version: string
): BatchedRecording[] {
	return data.map(item => (item.form === form ? { ...item, version } : item));
}
